import { IOrganizationControlOption } from '@/Interfaces/ControlOption.interface';
import { IAbortSignalThunkArg, IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import { IFormattedWhitelabelsResponse, IParsedWhitelabel } from '@/Interfaces/Whitelabels/Whitelabel.interface';
import { IWhitelabelFormData } from '@/Interfaces/Whitelabels/WhitelabelFormData.interface';
import { DEFAULT_WHITELABEL } from '@/constants/whitelabels';
import { formatWhitelabelsResponse, transformWhitelabelsFilters } from '@/helpers/Whitelabels';
import { RootState } from '@/store';
import { fetchWhitelabels } from '@/store/api/whitelabels.api';
import { SearchWhitelabelsFilters } from '@/types/whitelabels.types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { pendingCaseHandler, rejectedCaseHandler } from '../asyncThunkHandlers';

enum ActionPrefix {
  FETCH = 'whitelabels/getWhitelabels',
}

interface IState {
  selectedWhitelabel: IWhitelabelFormData;
  whitelabels: IParsedWhitelabel[];
  orgs: IOrganizationControlOption[];
  whitelabelsRequestId: boolean | string | null;
  total: number;
  filters: SearchWhitelabelsFilters;
}

const initialState: IState = {
  selectedWhitelabel: DEFAULT_WHITELABEL,
  whitelabels: [],
  orgs: [],
  whitelabelsRequestId: true,
  total: 0,
  filters: {
    page: 1,
    limit: 15,
    search: '',
    sort: [{ field: 'name', sort: 'asc' }],
    organizationIds: [],
  },
};

export const whitelabelSlice = createSlice({
  name: 'whitelabel',
  initialState,
  reducers: {
    setSelectedWhitelabel: (state, action) => {
      state.selectedWhitelabel = action.payload;
    },
    resetSelectedWhitelabel: state => {
      state.selectedWhitelabel = DEFAULT_WHITELABEL;
    },
    setFilters: (state, { payload }: { payload: Partial<SearchWhitelabelsFilters> }) => {
      state.filters = { ...state.filters, page: 1, ...payload };
    },
    resetWhitelabelsPageState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getWhitelabels.pending, pendingCaseHandler('whitelabels'))
      .addCase(getWhitelabels.rejected, rejectedCaseHandler('whitelabels'))
      .addCase(getWhitelabels.fulfilled, (state, action) => {
        if (state.whitelabelsRequestId === action.meta.requestId) {
          state.whitelabelsRequestId = null;
          state.whitelabels = action.payload.whitelabels;
          state.orgs = action.payload.orgs;
          state.total = action.payload.total || 0;
          if (!action.payload.whitelabels.length && state.filters.page > 1) {
            state.filters.page = 1;
          }
        }
      });
  },
});

export const { resetSelectedWhitelabel, setSelectedWhitelabel, resetWhitelabelsPageState, setFilters } =
  whitelabelSlice.actions;

export const getWhitelabels = createAsyncThunk<
  IFormattedWhitelabelsResponse,
  IAbortSignalThunkArg | undefined,
  IRejectValueThunkConfig
>(ActionPrefix.FETCH, async (payload, { getState, rejectWithValue }) => {
  const {
    whitelabels: { filters },
  } = getState() as RootState;

  const transformed = transformWhitelabelsFilters(filters);

  try {
    const response = await fetchWhitelabels(transformed, { signal: payload?.signal });
    const formatted = formatWhitelabelsResponse(response.data);
    return { ...formatted, total: response.meta?.total ?? 0 };
  } catch (err) {
    if (err instanceof Error) return rejectWithValue(err);
    return rejectWithValue(new Error('An unknown error occurred'));
  }
});

export default whitelabelSlice.reducer;
